<template>
  <div class="space-y-3">
    <!-- Quick buttons -->
    <div class="flex gap-2">
      <button
        type="button"
        class="flex-1 px-4 py-2.5 text-sm font-medium transition-colors rounded-lg cursor-pointer"
        :class="[
          isToday
            ? 'bg-white/20 text-white'
            : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
        ]"
        @click="selectToday">
        Today
      </button>
      <button
        type="button"
        class="flex-1 px-4 py-2.5 text-sm font-medium transition-colors rounded-lg cursor-pointer"
        :class="[
          isYesterday
            ? 'bg-white/20 text-white'
            : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
        ]"
        @click="selectYesterday">
        Yesterday
      </button>
    </div>

    <!-- Calendar -->
    <div
      @touchstart.passive="onTouchStart"
      @touchend.passive="onTouchEnd">
      <!-- Month/Year header with navigation -->
      <div class="flex items-center justify-between mb-2">
        <button
          type="button"
          class="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
          @click="prevMonth">
          <fa-icon :icon="['fas', 'chevron-left']" class="text-xs" />
        </button>
        <span class="text-sm font-medium text-white">{{ monthYearLabel }}</span>
        <button
          type="button"
          class="p-2 transition-colors rounded-lg"
          :class="
            isCurrentMonth
              ? 'text-gray-700 cursor-not-allowed'
              : 'text-gray-400 hover:text-white hover:bg-white/10'
          "
          :disabled="isCurrentMonth"
          @click="nextMonth">
          <fa-icon :icon="['fas', 'chevron-right']" class="text-xs" />
        </button>
      </div>

      <!-- Day of week headers -->
      <div class="grid grid-cols-7 mb-1">
        <div
          v-for="day in dayHeaders"
          :key="day"
          class="text-center text-2xs font-medium text-gray-500 py-1">
          {{ day }}
        </div>
      </div>

      <!-- Calendar grid - always 6 rows for consistent height -->
      <div class="grid grid-cols-7 grid-rows-6">
        <button
          v-for="day in calendarDays"
          :key="day.key"
          type="button"
          class="aspect-square flex items-center justify-center text-sm rounded-full transition-colors"
          :class="dayClasses(day)"
          :disabled="!day.selectable"
          @click="selectDate(day.dateStr)">
          {{ day.day }}
        </button>
      </div>
    </div>

    <!-- Clear date -->
    <button
      type="button"
      class="w-full px-4 py-2.5 text-sm font-medium transition-colors rounded-lg"
      :class="
        modelValue
          ? 'cursor-pointer text-white/40 bg-white/5 hover:bg-red-500/15 hover:text-red-400'
          : 'cursor-not-allowed text-white/15 bg-white/[0.02]'
      "
      :disabled="!modelValue"
      @click="clearDate">
      Clear date
    </button>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['update:modelValue'])

const dayHeaders = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

function toDateString(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const todayStr = computed(() => toDateString(new Date()))
const yesterdayStr = computed(() => {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return toDateString(d)
})

const isToday = computed(() => props.modelValue === todayStr.value)
const isYesterday = computed(() => props.modelValue === yesterdayStr.value)

// Calendar display state
const initDate = props.modelValue
  ? new Date(props.modelValue + 'T12:00:00')
  : new Date()
const displayYear = ref(initDate.getFullYear())
const displayMonth = ref(initDate.getMonth())

const monthYearLabel = computed(() => {
  const date = new Date(displayYear.value, displayMonth.value)
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
})

// Check if currently displaying the current month (to disable forward nav)
const isCurrentMonth = computed(() => {
  const now = new Date()
  return (
    displayYear.value === now.getFullYear() &&
    displayMonth.value === now.getMonth()
  )
})

function prevMonth() {
  if (displayMonth.value === 0) {
    displayMonth.value = 11
    displayYear.value--
  } else {
    displayMonth.value--
  }
}

function nextMonth() {
  if (isCurrentMonth.value) return
  if (displayMonth.value === 11) {
    displayMonth.value = 0
    displayYear.value++
  } else {
    displayMonth.value++
  }
}

const calendarDays = computed(() => {
  const year = displayYear.value
  const month = displayMonth.value
  const firstDayOfWeek = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const daysInPrevMonth = new Date(year, month, 0).getDate()

  const days = []

  // Previous month trailing days
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    days.push({
      day: daysInPrevMonth - i,
      dateStr: null,
      currentMonth: false,
      selectable: false,
      key: `prev-${daysInPrevMonth - i}`
    })
  }

  // Current month days
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = toDateString(new Date(year, month, d))
    days.push({
      day: d,
      dateStr,
      currentMonth: true,
      selectable: dateStr <= todayStr.value,
      key: `cur-${d}`
    })
  }

  // Always fill to 42 cells (6 rows) for consistent height
  while (days.length < 42) {
    const nextDay = days.length - firstDayOfWeek - daysInMonth + 1
    days.push({
      day: nextDay,
      dateStr: null,
      currentMonth: false,
      selectable: false,
      key: `next-${nextDay}`
    })
  }

  return days
})

function dayClasses(day) {
  if (!day.currentMonth) {
    return 'text-gray-700'
  }
  if (!day.selectable) {
    return 'text-gray-700 cursor-not-allowed'
  }
  const isSelected = props.modelValue === day.dateStr
  const isDayToday = day.dateStr === todayStr.value
  if (isSelected) {
    return 'bg-white/20 text-white font-semibold cursor-pointer hover:bg-white/25'
  }
  if (isDayToday) {
    return 'text-green-400 cursor-pointer hover:bg-white/10'
  }
  return 'text-white/70 cursor-pointer hover:bg-white/10'
}

function selectToday() {
  emit('update:modelValue', todayStr.value)
  const d = new Date()
  displayYear.value = d.getFullYear()
  displayMonth.value = d.getMonth()
}

function selectYesterday() {
  emit('update:modelValue', yesterdayStr.value)
  const d = new Date()
  d.setDate(d.getDate() - 1)
  displayYear.value = d.getFullYear()
  displayMonth.value = d.getMonth()
}

function selectDate(dateStr) {
  if (!dateStr) return
  emit('update:modelValue', dateStr)
}

function clearDate() {
  emit('update:modelValue', null)
}

// Swipe gesture for month navigation
let touchStartX = 0
let touchStartY = 0

function onTouchStart(e) {
  const touch = e.touches[0]
  touchStartX = touch.clientX
  touchStartY = touch.clientY
}

function onTouchEnd(e) {
  const touch = e.changedTouches[0]
  const dx = touch.clientX - touchStartX
  const dy = touch.clientY - touchStartY
  // Only trigger if horizontal swipe is dominant and > 50px
  if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
    if (dx > 0) {
      prevMonth()
    } else {
      nextMonth()
    }
  }
}
</script>
